// Tax provenance (NUB-315) — mirrors the backend priceInfo.taxSource field so the UI can
// tell users whether a tax figure is a real TaxJar result, a market-rate estimate, or not
// yet calculated. Kept as a small hand-written enum (not the generated types) so it works
// even before codegen is regenerated against the backend.
//
// CALCULATED: TaxJar ran (taxjarPayload present) — show the amount, even $0.00.
// ESTIMATED: no payload, but a market fallback rate > 0 was applied — a real estimated tax.
// TBC: no payload and no fallback rate (market rate 0 / new market) — nothing to estimate.
export enum TaxSource {
  CALCULATED = "CALCULATED",
  ESTIMATED = "ESTIMATED",
  TBC = "TBC",
}

// Derive provenance straight from a priceInfo (mirrors the backend resolveTaxSource). Use
// this at display time rather than trusting a stored priceInfo.taxSource: the client rebuilds
// priceInfo on recalc via calculatePossibleEjiPriceInfo, which has no real TaxJar payload — so
// its taxSource comes back ESTIMATED/TBC and would mislabel a Calculated invoice. The displayed
// priceInfo always carries the correct taxjarPayload/rates, so deriving here stays consistent.
export const deriveTaxSource = (priceInfo: any): TaxSource => {
  if (priceInfo?.taxjarPayload) return TaxSource.CALCULATED;
  const hasFallbackRate = (priceInfo?.taxjarPartsTaxRate ?? 0) > 0 || (priceInfo?.taxjarLaborTaxRate ?? 0) > 0;
  return hasFallbackRate ? TaxSource.ESTIMATED : TaxSource.TBC;
};

// Label for the tax line. Falls back to the plain "Tax" label when there's no provenance
// (backend field absent); non-taxable jobs/invoices are tax exempt (contact has an EIN).
export const taxLineLabel = (taxSource: TaxSource | string | null | undefined, taxable = true): string => {
  if (!taxable) return "Tax exempt";
  switch (taxSource) {
    case TaxSource.CALCULATED:
      return "Calculated Tax";
    case TaxSource.ESTIMATED:
      return "Estimated Tax";
    default:
      return "Tax";
  }
};

// True when the tax line should read "TBC" instead of an amount (taxable + no TaxJar and no
// fallback rate yet).
export const isTaxTbc = (taxSource: TaxSource | string | null | undefined, taxable = true): boolean =>
  taxable === true && taxSource === TaxSource.TBC;
